import type { ChangeEventHandler } from 'react'
import { FormValueScope } from '@epages/react-components'
import { forwardRef, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import Immutable from 'immutable'
import loadable from '@loadable/component'

import { imageUrl, resizedUrl, storeFile } from '../templateComponents/Workspace/daliConfig'
import { useTranslate, withComponents } from '../../utils/translate'
import FormValue from '../formComponents/FormValue'
import FormValueError from '../formComponents/FormValueError'
import ImageField from '../templateComponents/Workspace/plugins/image/ImageField'
import LogoAdjustmentThemeSettings from './ShopLogoAdjustmentSettings'
import SettingsLayer from '../templateComponents/Workspace/SettingsLayer'
import useHelpCenterLink from '../../utils/hooks/useHelpCenterLink'

const LogoCropSettings = loadable(() => import(/* webpackChunkName: "editor" */ './LogoCropSettings'))

const recommendedImageWidth = 200
const recommendedImageHeight = 200

type RadioOptionProps = {
  name: string
  checked: boolean
  onChange: ChangeEventHandler<HTMLInputElement>
} & TranslateProps

function RadioOption({ t, name, checked, onChange }: Readonly<RadioOptionProps>) {
  return (
    <label>
      <input type="radio" name={name} checked={checked} onChange={onChange} />
      {t(`${name}.label`)}
    </label>
  )
}

type ConfigType = {
  storeFile: (file: File) => Promise<Storage.Image>
  imageUrl?: (src: string) => string
  previewUrl?: (src: string) => string
}

type ImageLogoSettingsProps = {
  config: ConfigType
  toggleCropSettings: VoidFunction
  hasImage: boolean
  disabled: boolean
} & TranslateProps

const ImageLogoSettings = forwardRef<HTMLDivElement, Readonly<ImageLogoSettingsProps>>(
  ({ t, config, toggleCropSettings, hasImage, disabled }, ref) => {
    const [error, setError] = useState<Error | null>(null)
    const helpCenterUrl = useHelpCenterLink('IMAGE_FORMATS')
    const isUnsupportedMediaType = error?.code === 415

    return (
      <fieldset disabled={disabled}>
        {error && (
          <div className="dali-notification-danger">
            {error.message}
            {isUnsupportedMediaType && (
              <a
                href={helpCenterUrl}
                target="_blank"
                className="ep-form-row-text-external-link"
                rel="noreferrer noopener"
              >
                {t(':components.imageUploadComponent.imageField.errorMessages.helpCenterLink')}
              </a>
            )}
          </div>
        )}
        <FormValueError name="imageData.src">
          {(errorMessage: string) =>
            errorMessage ? <div className="dali-notification-danger">{errorMessage}</div> : null
          }
        </FormValueError>
        <div className="ep-form-row">
          <div className="dali-settingslayer-element">
            <div className="dali-settingslayer-hint">
              {t('shopLogoRadioButton.imageInfo', { recommendedImageWidth, recommendedImageHeight })}
            </div>
          </div>
        </div>
        <div className="ep-form-row" ref={ref}>
          <ImageField
            name="imageData"
            storeFile={config.storeFile}
            imageUrl={config.imageUrl}
            previewUrl={config.previewUrl}
            onError={(error: Error) => setError(error)}
            onChange={() => setError(null)}
            renderCropButton={() => (
              <button
                type="button"
                disabled={disabled}
                className="dali-grid-element-edit-button dali-grid-element-edit-button-crop"
                title={t('cropButton.label')}
                onClick={() => toggleCropSettings()}
              />
            )}
          />
        </div>
        <div className="ep-form-row">
          <div className="dali-settingslayer-logo-adjustments">
            <LogoAdjustmentThemeSettings disabled={!hasImage || disabled} linkDisabled={disabled} />
          </div>
        </div>
      </fieldset>
    )
  },
)
ImageLogoSettings.displayName = 'ImageLogoSettings'

type CropLayerProps = {
  config: ConfigType
  value: ImmutableMap
  onSaveCropResult: (data: ImmutableMap) => void
  onCancelCrop: () => void
  isOpen: boolean
}
function CropLayer({ config, value, onSaveCropResult, onCancelCrop, isOpen }: Readonly<CropLayerProps>) {
  return (
    <SettingsLayer
      open={isOpen}
      onEscapeKeyDown={onCancelCrop}
      className="dali-settingslayer-image-crop"
      side="top"
      align="center"
    >
      {({ renderLayout }) => (
        <LogoCropSettings
          data={value}
          onCancel={onCancelCrop}
          onSave={onSaveCropResult}
          {...{ renderLayout, config }}
        />
      )}
    </SettingsLayer>
  )
}
export default function ShopLogo(_props) {
  const store: any = useStore()
  const isBeyond = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))
  const shopSlug = useSelector<State, string>((state) => state.getIn(['shop', 'slug']))

  const t = useTranslate('interface', 'components.bottomBar.headerSettings.shopLogo')

  const config: ConfigType = {
    storeFile: storeFile(store.api),
    imageUrl: imageUrl(shopSlug),
    previewUrl: resizedUrl(680),
  }

  const [cropSettingsActive, setCropSettingsActive] = useState(false)

  const toggleCropSettings = () => {
    setCropSettingsActive(!cropSettingsActive)
  }

  const shopSettingsUrl = isBeyond
    ? `${mboBaseUrl}/settings/website`
    : `${mboBaseUrl}?ViewAction=UnityMBO-ViewStatusGeneral`

  return (
    <FormValue name="shopLogo">
      {(shopLogo: ImmutableMap, setShopLogo: (data: ImmutableMap) => void) => {
        const shouldHideLogo = shopLogo.get('shouldHideLogo')

        const onSaveCropResult = (data: ImmutableMap) => {
          setShopLogo(shopLogo.setIn(['imageData'], data))
          setCropSettingsActive(false)
        }

        const onCancelCrop = () => {
          setCropSettingsActive(false)
        }

        return (
          <>
            <CropLayer
              value={shopLogo.get('imageData')}
              isOpen={cropSettingsActive}
              {...{ config, onSaveCropResult, onCancelCrop }}
            />
            <div className="dali-settingslayer-element">{t('description')}</div>
            <RadioOption
              name="shopNameRadioButton"
              t={t}
              checked={shouldHideLogo}
              onChange={() => setShopLogo(shopLogo.set('shouldHideLogo', true))}
            />
            <div className="ep-form-row" data-disabled={!shouldHideLogo || null}>
              <div className="dali-settingslayer-element">
                {withComponents(t)('shopNameRadioButton.infoText', {
                  link: (
                    <a className="ep-form-row-text-link" href={shopSettingsUrl} target="_parent">
                      {t('shopNameRadioButton.linkText')}
                    </a>
                  ),
                })}
              </div>
            </div>
            <RadioOption
              name="shopLogoRadioButton"
              t={t}
              checked={!shouldHideLogo}
              onChange={() => setShopLogo(shopLogo.set('shouldHideLogo', false))}
            />

            <FormValueScope name="shopLogo">
              <ImageLogoSettings
                t={t}
                hasImage={Boolean(shopLogo.getIn(['imageData', 'src']))}
                disabled={shouldHideLogo}
                {...{ config, toggleCropSettings }}
              />
            </FormValueScope>
          </>
        )
      }}
    </FormValue>
  )
}
ShopLogo.defaultValue = Immutable.fromJS({
  imageData: {},
  shouldHideLogo: false,
})
